import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import { NEWS_CATEGORIES } from "@/types/media";
import type { NewsCategory } from "@/types/media";

const PAGE_SIZE = 5;
type CategoryFilter = "全部" | NewsCategory;

export default function News() {
  const [category, setCategory] = useState<CategoryFilter>("全部");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  usePageMeta(
    "影视资讯 - 茶杯狐影视",
    "茶杯狐影视资讯频道，汇集电影资讯、电视剧资讯与行业动态，及时了解最新影视热点与市场趋势。",
  );

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    return newsList
      .filter((item) => category === "全部" || item.category === category)
      .filter(
        (item) =>
          !k ||
          item.title.toLowerCase().includes(k) ||
          item.summary.toLowerCase().includes(k),
      )
      .sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
  }, [category, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const changeCategory = (next: CategoryFilter) => {
    setCategory(next);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
  };

  const reset = () => {
    setCategory("全部");
    setKeyword("");
    setPage(1);
  };

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="transition-colors hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-foreground-700">影视资讯</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            影视资讯
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            聚焦电影资讯、电视剧资讯与行业动态，为你梳理最新影视热点与市场观察。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {(["全部", ...NEWS_CATEGORIES] as CategoryFilter[]).map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => changeCategory(cat)}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                  category === cat
                    ? "bg-primary-600 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="flex w-full items-center rounded-md border border-background-300 bg-background-50 px-3 lg:w-72">
            <i className="ri-search-line text-foreground-400" aria-hidden="true" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="搜索资讯标题或摘要"
              className="w-full bg-transparent px-2 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword ? (
              <button
                type="button"
                onClick={() => handleSearch("")}
                aria-label="清空"
                className="cursor-pointer text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-circle-line" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <span className="font-bold text-primary-700">{filtered.length}</span> 条资讯
        </p>

        {paged.length > 0 ? (
          <div className="mt-4 flex flex-col gap-4">
            {paged.map((item, index) => (
              <NewsCard key={item.id} item={item} index={index} showCover />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-newspaper-line text-3xl" aria-hidden="true" />
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
              没有找到相关资讯
            </h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              换个关键词或分类试试，也可以点击下方按钮查看全部资讯。
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-6 flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              查看全部资讯
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}